/**
 * Service-worker registration with friendly update prompts.
 *
 * Behaviour:
 *  - Registers `/service-worker.js` once the page is fully loaded (so it never
 *    blocks first paint).
 *  - When a NEW SW takes over (after an existing one was already controlling),
 *    fires `onUpdate(reg)` so the UI can show a "new version available" toast.
 *  - Exposes `installAvailable` event so the InstallAppPrompt component can
 *    react to Chrome's `beforeinstallprompt`.
 */

const isLocalhost = Boolean(
    window.location.hostname === "localhost"
    || window.location.hostname === "[::1]"
    || window.location.hostname.match(/^127(?:\.\d{1,3}){3}$/),
);

export const register = ({ onSuccess, onUpdate } = {}) => {
    // Only run service workers in production-grade contexts (https/localhost).
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    // Don't register on http; SW requires a secure context.
    if (window.location.protocol !== "https:" && !isLocalhost) return;

    window.addEventListener("load", () => {
        const swUrl = `${process.env.PUBLIC_URL || ""}/service-worker.js`;
        navigator.serviceWorker.register(swUrl).then((reg) => {
            reg.onupdatefound = () => {
                const installing = reg.installing;
                if (!installing) return;
                installing.onstatechange = () => {
                    if (installing.state === "installed") {
                        if (navigator.serviceWorker.controller) {
                            // Newer SW available; old one still serving.
                            if (onUpdate) onUpdate(reg);
                        } else {
                            // First install.
                            if (onSuccess) onSuccess(reg);
                        }
                    }
                };
            };
        }).catch((err) => {
            console.debug("[sw] register failed:", err);
        });
    });
};

export const unregister = () => {
    if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready
            .then((reg) => reg.unregister())
            .catch((err) => console.debug("[sw] unregister failed:", err));
    }
};

/**
 * Captures the `beforeinstallprompt` event so the React tree can show a
 * brutalist install CTA at the right moment.  Resolves to a function that lets
 * the consumer trigger the native install prompt, or `null` if the browser
 * doesn't support installs (Safari iOS, older browsers).
 */
let _deferredPrompt = null;
const _listeners = new Set();

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        _deferredPrompt = e;
        _listeners.forEach((cb) => cb(true));
    });
    window.addEventListener("appinstalled", () => {
        _deferredPrompt = null;
        _listeners.forEach((cb) => cb(false));
    });
}

export const onInstallAvailability = (cb) => {
    _listeners.add(cb);
    cb(!!_deferredPrompt);
    return () => _listeners.delete(cb);
};

export const triggerInstallPrompt = async () => {
    if (!_deferredPrompt) return { outcome: "unavailable" };
    _deferredPrompt.prompt();
    const choice = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    _listeners.forEach((cb) => cb(false));
    return choice; // { outcome: "accepted" | "dismissed", platform: "..." }
};

/** True when the app is already running as a PWA (standalone window). */
export const isStandalone = () => {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator.standalone === true
    );
};
